import React from 'react';
import { motion } from 'framer-motion';

interface IconPillButtonProps {
  icon: React.ReactNode;
  ariaLabel: string;
  active?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  className?: string;
  size?: 'sm' | 'md';
}

export default function IconPillButton({
  icon,
  ariaLabel,
  active = false,
  disabled = false,
  onClick,
  className = '',
  size = 'md'
}: IconPillButtonProps) {
  const sizeClasses = size === 'sm' ? 'w-10 h-10' : 'w-11 h-11';
  const iconSize = size === 'sm' ? 'text-[18px]' : 'text-[20px]';

  return (
    <motion.button
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-pressed={active}
      whileHover={!disabled ? { scale: 1.05 } : undefined}
      whileTap={!disabled ? { scale: 0.95 } : undefined}
      className={`
        relative inline-flex items-center justify-center
        ${sizeClasses} rounded-full
        bg-theme-cards/60 backdrop-blur-md
        transition-all duration-200
        focus:outline-none focus:ring-2 focus:ring-pink-500/60 focus:ring-offset-2 focus:ring-offset-theme-background
        ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
        ${className}
      `}
      style={{
        borderWidth: '1.5px',
        borderStyle: 'solid',
        borderImage: 'linear-gradient(135deg, #ff3148, #00d1ff) 1',
        boxShadow: active
          ? '0 0 16px rgba(255, 49, 72, 0.4), 0 4px 6px rgba(0, 0, 0, 0.1)'
          : '0 2px 4px rgba(0, 0, 0, 0.1)',
        background: active
          ? 'linear-gradient(135deg, rgba(255, 49, 72, 0.1), rgba(0, 209, 255, 0.1))'
          : undefined
      }}
    >
      <div
        className={`
          ${iconSize}
          transition-colors duration-200
          ${active ? 'text-white' : 'text-white/80'}
          ${!disabled && 'group-hover:text-white'}
        `}
      >
        {icon}
      </div>
    </motion.button>
  );
}
